import { useEffect, useState } from 'react'
import { getActiveInstances, getHlsToken } from '../api/endpoints'
import { usePlayer } from '../provider/playerSource'
import { useScreenShare } from '../provider/screenShare'
import { SourceType } from '../const/SourceType'
import type { ActiveInstance } from '@expostreamer/shared-types'
import styles from './style'

function flattenPlayable(instances: ActiveInstance[]): ActiveInstance[] {
  return instances.flatMap(item => [
    ...(item.outputType !== SourceType.NONE ? [item] : []),
    ...(item.children ? flattenPlayable(item.children) : []),
  ])
}

// Screen shares aren't ActiveInstances (no ffmpeg process behind them), so
// they're kept as a separate option group rather than merged into the same
// flattened list.
const SHARE_PREFIX = 'share:'

function StreamPicker() {
  const [instanceList, setInstanceList] = useState<ActiveInstance[]>([])
  const [hlsToken, setHlsToken] = useState<string | null>(null)
  const [selected, setSelected] = useState('')
  const { setVideoSource } = usePlayer()
  const { activeShares } = useScreenShare()

  useEffect(() => {
    const refresh = () => {
      getActiveInstances().then(({ ok, data }) => {
        if (ok) setInstanceList(data)
      })
    }
    refresh()
    const interval = setInterval(refresh, 5000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    getHlsToken().then(({ ok, data }) => {
      if (ok) setHlsToken(data.token)
    })
  }, [])

  const playableInstances = flattenPlayable(instanceList)
  const hasOptions = playableInstances.length > 0 || activeShares.length > 0

  function handleChange(value: string) {
    setSelected(value)

    if (value.startsWith(SHARE_PREFIX)) {
      setVideoSource({ type: SourceType.WEBRTC, url: value.slice(SHARE_PREFIX.length) })
      return
    }

    const item = playableInstances.find(instance => instance.instanceName === value)
    if (!item) return

    setVideoSource({
      type: item.outputType,
      url:
        item.outputType === SourceType.HLS
          ? './live/playlist.m3u8?token=' + hlsToken
          : item.instanceName,
      videoCodec: item.videoCodec,
      audioCodec: item.audioCodec,
    })
  }

  return (
    <select
      style={styles.streamPicker}
      value={selected}
      disabled={!hasOptions}
      onChange={event => handleChange(event.target.value)}
    >
      <option
        value=''
        disabled
      >
        {hasOptions ? 'Select a stream…' : 'No streams available'}
      </option>
      {playableInstances.map(item => (
        <option
          key={item.instanceName}
          value={item.instanceName}
        >
          {item.instanceName}
        </option>
      ))}
      {activeShares.map(share => (
        <option
          key={share.shareId}
          value={SHARE_PREFIX + share.shareId}
        >
          {share.ownerUsername + "'s screen"}
        </option>
      ))}
    </select>
  )
}

export default StreamPicker
